import React, { useState, useMemo, useEffect } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { debounce } from "lodash";

type DebouncedTextFieldProps = Omit<TextFieldProps, 'onChange'> & {
  value: string;
  onDebouncedChange: (value: string) => void;
  debounceDelay?: number;
};

export default function DebouncedTextField({ 
  value, 
  onDebouncedChange, 
  debounceDelay = 300, 
  ...props 
}: DebouncedTextFieldProps) {
  const [localEdit, setLocalEdit] = useState<{ sourceValue: string; value: string }>();
  const localValue = localEdit?.sourceValue === value ? localEdit.value : value;

  const pushToParent = useMemo(() =>
    debounce((newVal: string) => {
      onDebouncedChange(newVal);
    }, debounceDelay),
    [onDebouncedChange, debounceDelay]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalEdit({ sourceValue: value, value: e.target.value }); // Instant local update for fast typing
    pushToParent(e.target.value);  // Delayed parent update
  };

  useEffect(() => {
    return () => pushToParent.cancel();
  }, [pushToParent]);

  return <TextField {...props} value={localValue} onChange={handleChange} />;
}
